import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ModalOverlayProps {
  onClose: () => void;
  className?: string;
  children: ReactNode;
}

/**
 * Whether any modal is currently open. Global keyboard handlers use this to
 * stand down while a modal is up (modals own their own keys). Owned here so
 * "what marks an open modal in the DOM" stays a ModalOverlay implementation
 * detail rather than a class-name string other files must know.
 */
export function isModalOpen(): boolean {
  return document.querySelector('.modal-backdrop') !== null;
}

export function ModalOverlay({ onClose, className, children }: ModalOverlayProps) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // A drag that starts inside the dialog (e.g. selecting input text) and ends
  // over the backdrop dispatches `click` on the backdrop, their common
  // ancestor. Only a press that also began on the backdrop means "outside".
  const pressedOnBackdrop = useRef(false);

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(e) => { pressedOnBackdrop.current = e.target === e.currentTarget; }}
      onClick={(e) => { if (pressedOnBackdrop.current && e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={['modal-content', className].filter(Boolean).join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
